'use client';

import { useState, useMemo } from 'react';
import { CareerPath } from '@/lib/careerPaths';
import {
  generateCoverLetter,
  generateFullCoverLetterText,
  CoverLetter,
} from '@/lib/coverLetterGenerator';
import styles from './CoverLetterGenerator.module.css';

interface CoverLetterGeneratorProps {
  career: CareerPath;
  userSegment?: string | null;
}

export default function CoverLetterGenerator({
  career,
  userSegment = null
}: CoverLetterGeneratorProps) {
  const [activeTab, setActiveTab] = useState<'template' | 'tips' | 'checklist'>('template');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Generate the cover letter
  const letter: CoverLetter = useMemo(() => {
    return generateCoverLetter(career, userSegment);
  }, [career, userSegment]);

  const fullText = useMemo(() => {
    return generateFullCoverLetterText(letter);
  }, [letter]);

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const tabs = [
    { id: 'template' as const, label: 'Template', icon: '📝' },
    { id: 'tips' as const, label: 'Do & Don\'t', icon: '💡' },
    { id: 'checklist' as const, label: 'Checklist', icon: '✅' },
  ];

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>📄</div>
        <div className={styles.headerContent}>
          <h3>Cover Letter Builder</h3>
          <p>Customizable template for {career.title} applications</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={styles.tabNav}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            <span className={styles.tabLabel}>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Template Tab */}
      {activeTab === 'template' && (
        <div className={styles.templateSection}>
          {/* Copy All Button */}
          <button
            className={styles.copyAllButton}
            onClick={() => copyToClipboard(fullText, 'full-letter')}
          >
            {copiedId === 'full-letter' ? '✓ Copied Full Letter!' : '📋 Copy Complete Letter'}
          </button>

          {/* Greeting */}
          <div className={styles.paragraphBlock}>
            <div className={styles.paragraphHeader}>
              <span className={styles.paragraphLabel}>Greeting</span>
              <button
                className={styles.copyButton}
                onClick={() => copyToClipboard(letter.template.greeting, 'greeting')}
              >
                {copiedId === 'greeting' ? '✓' : '📋'}
              </button>
            </div>
            <p className={styles.paragraphText}>{letter.template.greeting}</p>
          </div>

          {/* Paragraphs */}
          {letter.paragraphs.map((para) => (
            <div key={para.id} className={styles.paragraphBlock}>
              <div className={styles.paragraphHeader}>
                <span className={styles.paragraphLabel}>{para.title}</span>
                <span className={`${styles.paragraphType} ${styles[`type${para.type}`]}`}>
                  {para.type}
                </span>
                <button
                  className={styles.copyButton}
                  onClick={() => copyToClipboard(para.content, para.id)}
                >
                  {copiedId === para.id ? '✓' : '📋'}
                </button>
              </div>
              <div className={styles.paragraphContent}>
                {para.content.split('\n').map((line, idx) => (
                  <p key={idx}>{line}</p>
                ))}
              </div>
              <div className={styles.paragraphTip}>
                <span className={styles.tipIcon}>💡</span>
                <span>{para.tips}</span>
              </div>
            </div>
          ))}

          {/* Signature */}
          <div className={styles.paragraphBlock}>
            <div className={styles.paragraphHeader}>
              <span className={styles.paragraphLabel}>Closing & Signature</span>
              <button
                className={styles.copyButton}
                onClick={() => copyToClipboard(`Sincerely,\n\n${letter.template.signature}`, 'signature')}
              >
                {copiedId === 'signature' ? '✓' : '📋'}
              </button>
            </div>
            <p className={styles.paragraphText}>Sincerely,</p>
            <div className={styles.signature}>
              {letter.template.signature.split('\n').map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
            </div>
          </div>

          {/* Power Phrases */}
          <div className={styles.phrasesSection}>
            <h4>Power Phrases to Include</h4>
            <div className={styles.phrasesList}>
              {letter.powerPhrases.map((phrase, idx) => (
                <span
                  key={idx}
                  className={styles.powerPhrase}
                  onClick={() => copyToClipboard(phrase, `power-${idx}`)}
                  title="Click to copy"
                >
                  {phrase}
                  {copiedId === `power-${idx}` && <span className={styles.copiedBadge}>✓</span>}
                </span>
              ))}
            </div>
          </div>

          {/* Placeholder Note */}
          <div className={styles.placeholderNote}>
            <span className={styles.noteIcon}>✏️</span>
            <div>
              <strong>Customize before sending:</strong> Replace all text in [BRACKETS] with your specific details. Each letter should feel personalized to the company.
            </div>
          </div>
        </div>
      )}

      {/* Tips Tab */}
      {activeTab === 'tips' && (
        <div className={styles.tipsSection}>
          {/* Do's */}
          <div className={styles.tipsGroup}>
            <h4 className={styles.tipsGroupTitle}>
              <span className={styles.doIcon}>✓</span> Do This
            </h4>
            <ul className={styles.tipsList}>
              {letter.tips
                .filter((t) => t.category === 'do')
                .map((tip, idx) => (
                  <li key={idx} className={styles.doTip}>{tip.text}</li>
                ))}
            </ul>
          </div>

          {/* Don'ts */}
          <div className={styles.tipsGroup}>
            <h4 className={styles.tipsGroupTitle}>
              <span className={styles.dontIcon}>✗</span> Avoid This
            </h4>
            <ul className={styles.tipsList}>
              {letter.tips
                .filter((t) => t.category === 'dont')
                .map((tip, idx) => (
                  <li key={idx} className={styles.dontTip}>{tip.text}</li>
                ))}
            </ul>
          </div>

          {/* Avoid Phrases */}
          <div className={styles.avoidPhrasesSection}>
            <h4>Phrases to Avoid</h4>
            <div className={styles.avoidPhrasesList}>
              {letter.avoidPhrases.map((phrase, idx) => (
                <div key={idx} className={styles.avoidPhrase}>
                  <span className={styles.strikeThrough}>{phrase.split(' - ')[0]}</span>
                  {phrase.includes(' - ') && (
                    <span className={styles.avoidReason}>{phrase.split(' - ')[1]}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Format Tips */}
          <div className={styles.tipsGroup}>
            <h4 className={styles.tipsGroupTitle}>
              <span className={styles.formatIcon}>📐</span> Format & Style
            </h4>
            <ul className={styles.tipsList}>
              {letter.tips
                .filter((t) => t.category === 'format')
                .map((tip, idx) => (
                  <li key={idx} className={styles.formatTip}>{tip.text}</li>
                ))}
            </ul>
          </div>
        </div>
      )}

      {/* Checklist Tab */}
      {activeTab === 'checklist' && (
        <div className={styles.checklistSection}>
          <p className={styles.checklistIntro}>
            Before you send, make sure your cover letter passes these checks:
          </p>

          <div className={styles.checklistItems}>
            {letter.checklist.map((item, idx) => (
              <label key={idx} className={styles.checklistItem}>
                <input type="checkbox" className={styles.checklistCheckbox} />
                <span className={styles.checklistText}>{item.replace('✓ ', '')}</span>
              </label>
            ))}
          </div>

          <div className={styles.finalReminder}>
            <h4>Final Reminder</h4>
            <p>
              A cover letter that could be sent to any company isn&apos;t worth sending.
              Take the extra 10 minutes to personalize each letter to the specific
              company and role. Mention something specific about their work, culture,
              or recent news that shows you&apos;ve done your research.
            </p>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className={styles.footer}>
        <p>
          <strong>Pro Tip:</strong> Save your cover letter as [YourName]_[CompanyName]_CoverLetter.pdf for easy organization and professional appearance.
        </p>
      </div>
    </div>
  );
}
